import { fromEvent, Observable, race } from "rxjs";
import { map, take } from "rxjs/operators";
import { DEFAULT_COMPRESS } from "./constants";
import type { ClipUploadImageCompressOptions, ClipUploadProps } from "./types";

export type PreloadResult = "loaded" | "error";

export const createPreload$ = (serverUrl: string, mimeType: string | undefined): Observable<PreloadResult> => {
  return new Observable<PreloadResult>((subscriber) => {
    const isImage = !!mimeType?.startsWith("image/");
    const isVideo = !!mimeType?.startsWith("video/");

    if ((!isImage && !isVideo) || !serverUrl) {
      subscriber.next("loaded");
      subscriber.complete();
      return;
    }

    const el: HTMLImageElement | HTMLVideoElement = isImage ? new Image() : document.createElement("video");
    if (el instanceof HTMLVideoElement) {
      el.preload = "auto";
      el.muted = true;
      el.playsInline = true;
    }

    const loaded$ = fromEvent(el, isImage ? "load" : "loadeddata").pipe(map(() => "loaded" as const));
    const error$ = fromEvent(el, "error").pipe(map(() => "error" as const));

    const inner = race(loaded$, error$).pipe(take(1)).subscribe(subscriber);

    el.src = serverUrl;

    return () => {
      inner.unsubscribe();
      el.removeAttribute("src");
      if (el instanceof HTMLVideoElement) el.load();
    };
  });
};

export const generateUid = (): string => `clip-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException ? error.name === "AbortError" : error instanceof Error && error.name === "AbortError";

export const matchesAccept = (file: File, accept: string[]): boolean => {
  if (accept.length === 0) return true;
  const fileType = (file.type || "").toLowerCase();
  const fileName = (file.name || "").toLowerCase();
  return accept.some((rawPattern) => {
    const pattern = rawPattern.trim().toLowerCase();
    if (!pattern) return false;
    if (pattern.startsWith(".")) return fileName.endsWith(pattern);
    if (pattern.endsWith("/*")) return fileType.startsWith(pattern.slice(0, -1));
    return fileType === pattern;
  });
};

export const resolveCompressOptions = (
  compressImage: ClipUploadProps["compressImage"],
): Required<ClipUploadImageCompressOptions> | null => {
  if (!compressImage) return null;
  if (compressImage === true) return DEFAULT_COMPRESS;
  return { ...DEFAULT_COMPRESS, ...compressImage };
};
